/**
 * Speaker identity across videos (archive mode).
 *
 * AssemblyAI labels speakers PER FILE, so "A" in debate 1 and "C" in debate 2 can both be Trump.
 * The merge gives each source-speaker a default label ("A·1" = speaker A of sheet 1). This layer
 * lets the user say "these are the same person": an identity map
 *     { "<transcriptId>:<rawSpeaker>": "Trump", ... }
 * resolves each row's `speaker` to the name. Two entries with the SAME name are one speaker
 * everywhere the ET reads `speaker` — the filter chips, the headings, the AI pack, the export.
 *
 * The UX is the rename the user already knows: give "A·1" and "C·2" the same name and they
 * link; blank the name to go back to the default; the Speakers panel unlinks one member.
 *
 * `applySpeakerIdentities` MUTATES `speaker` on the row objects on purpose: originalRows and
 * rows share objects in the ET, and mutating (then bumping arrays by reference) means a rename
 * never re-ingests, so filters and ticks survive it.
 */

const KEY_SEP = ":";

/** `"6:A"` for a merged row; null for spacer rows / rows with no speaker / single-ET rows. */
export const speakerKeyFor = (row) => {
  if (!row || row.space || !row.source) return null;
  const raw = row.rawSpeaker;
  if (raw == null || raw === "") return null;
  return `${row.source.transcriptId}${KEY_SEP}${raw}`;
};

export const speakerKeyParts = (key) => {
  const s = String(key || "");
  const i = s.indexOf(KEY_SEP);
  if (i <= 0) return null;
  return { transcriptId: s.slice(0, i), rawSpeaker: s.slice(i + 1) };
};

/** The label a merged row shows when it is not linked: "A·1". */
export const defaultSpeakerLabel = (row) => `${row.rawSpeaker}·${row.source.sheetNum}`;

const clean = (v) => (v == null ? "" : String(v).trim());

/** What this row's `speaker` should read under the map. Non-archive rows pass through. */
export const resolveSpeaker = (row, map) => {
  const key = speakerKeyFor(row);
  if (!key) return row ? row.speaker : undefined;
  const name = map ? clean(map[key]) : "";
  return name || defaultSpeakerLabel(row);
};

/** Apply the map to every row in place. Returns how many rows changed. */
export function applySpeakerIdentities(rows, map) {
  let changed = 0;
  (rows || []).forEach((row) => {
    if (!row || row.space) return;
    const next = resolveSpeaker(row, map);
    if (next !== row.speaker) { row.speaker = next; changed += 1; }
  });
  return changed;
}

/**
 * Rename whatever is CURRENTLY displayed as `currentLabel` to `newName` — every source-speaker
 * behind that label moves together (so renaming "Trump" renames him in every video). Blank name
 * = back to the per-source default. Returns a NEW map.
 */
export function renameSpeakerIdentity(rows, map, currentLabel, newName) {
  const next = { ...(map || {}) };
  const name = clean(newName);
  const keys = new Set();
  (rows || []).forEach((row) => {
    const key = speakerKeyFor(row);
    if (!key) return;
    if (resolveSpeaker(row, map) === currentLabel) keys.add(key);
  });
  keys.forEach((key) => { if (name) next[key] = name; else delete next[key]; });
  return next;
}

/** Drop one member from its identity (back to "A·2"). Returns a NEW map. */
export function unlinkSpeaker(map, key) {
  const next = { ...(map || {}) };
  delete next[key];
  return next;
}

/**
 * Group the archive's source-speakers by what they currently display as — the Speakers panel.
 * @returns {Array<{ label, linked, members: Array<{ key, transcriptId, rawSpeaker, sheetNum, short, sourceLabel, color, clips }> }>}
 */
export function speakerGroups(rows, map) {
  const byKey = new Map();
  (rows || []).forEach((row) => {
    const key = speakerKeyFor(row);
    if (!key) return;
    let m = byKey.get(key);
    if (!m) {
      m = {
        key,
        transcriptId: row.source.transcriptId,
        rawSpeaker: row.rawSpeaker,
        sheetNum: row.source.sheetNum,
        short: row.source.short,
        sourceLabel: row.source.label,
        color: row.source.color,
        label: resolveSpeaker(row, map),
        clips: 0,
      };
      byKey.set(key, m);
    }
    m.clips += 1;
  });
  const groups = new Map();
  [...byKey.values()]
    .sort((a, b) => (a.sheetNum - b.sheetNum) || String(a.rawSpeaker).localeCompare(String(b.rawSpeaker)))
    .forEach((m) => {
      if (!groups.has(m.label)) groups.set(m.label, { label: m.label, members: [] });
      groups.get(m.label).members.push(m);
    });
  return [...groups.values()].map((g) => ({
    ...g,
    linked: new Set(g.members.map((m) => String(m.transcriptId))).size > 1,
  }));
}

export const identityStorageKey = (archiveKey) => `cc_archive_speakers_${archiveKey}`;

const storageOf = (storage) => storage || (typeof window !== "undefined" ? window.localStorage : null);

export function loadIdentityMap(archiveKey, storage) {
  try {
    const s = storageOf(storage);
    if (!s || !archiveKey) return {};
    const raw = s.getItem(identityStorageKey(archiveKey));
    const obj = raw ? JSON.parse(raw) : null;
    return obj && typeof obj === "object" && !Array.isArray(obj) ? obj : {};
  } catch (e) { return {}; }
}

export function saveIdentityMap(archiveKey, map, storage) {
  try {
    const s = storageOf(storage);
    if (!s || !archiveKey) return false;
    s.setItem(identityStorageKey(archiveKey), JSON.stringify(map || {}));
    return true;
  } catch (e) { return false; }
}
